import {
  Bot,
  GitBranch,
  GitCommitHorizontal,
  Puzzle,
  Settings,
  Shield,
  SquareTerminal,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { desktop } from "./bridge";

/**
 * Edições do Kompil (ADR 0010). O produto completo é o app desktop; a web é a versão resumida,
 * para editar código e conversar de qualquer lugar.
 *
 * Em desenvolvimento, `localStorage["kompil.edition"] = "desktop"` mostra o produto completo no
 * navegador (testes e2e e capturas de tela).
 */
export type DesktopFeature =
  "terminal" | "ai" | "git" | "history" | "network" | "groups" | "plugins" | "settings" | "admin";

function previewFullEdition(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem("kompil.edition") === "desktop";
  } catch {
    return false;
  }
}

/** true no app desktop: tudo liberado. */
export const FULL_EDITION = desktop !== null || previewFullEdition();

/**
 * O que a web também tem: conversas (sempre), grupos, os projetos salvos na nuvem e o terminal NA
 * NUVEM (o contêiner do Kompil). Nada que dependa do computador da pessoa (cópia local, terminal na
 * máquina, pastas abertas, Live Server) existe na web: sem o app, só há o terminal do sandbox.
 */
const WEB_FEATURES: ReadonlySet<DesktopFeature> = new Set<DesktopFeature>(["groups", "terminal"]);

export const hasFeature = (feature: DesktopFeature) => FULL_EDITION || WEB_FEATURES.has(feature);

/**
 * Na web, o projeto vive só na nuvem: nada fica guardado no navegador para abrir sem internet
 * (isso é do app desktop, que tem a cópia local). Apaga o que versões antigas guardaram.
 */
export function clearWebOfflineCopies() {
  if (FULL_EDITION || typeof indexedDB === "undefined") return;
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("kompil.sala.")) localStorage.removeItem(key);
    }
  } catch {
    // sem armazenamento
  }
  void indexedDB
    .databases?.()
    .then((dbs) => {
      for (const db of dbs) if (db.name?.startsWith("kompil:")) indexedDB.deleteDatabase(db.name);
    })
    .catch(() => undefined);
}

export const DESKTOP_FEATURES: Record<
  DesktopFeature,
  { title: string; description: string; icon: LucideIcon }
> = {
  terminal: {
    title: "Terminal e execução",
    description: "Seu próprio ambiente isolado: rode, teste e depure o projeto sem sair do editor.",
    icon: SquareTerminal,
  },
  ai: {
    title: "IA no editor",
    description:
      "Assistente com o contexto do projeto, autocomplete, revisão em segundo plano e o Agente Claude.",
    icon: Bot,
  },
  git: {
    title: "Git e GitHub",
    description: "Commit, push e pull direto do editor, importar repositórios e ver o diff de cada arquivo.",
    icon: GitBranch,
  },
  history: {
    title: "Histórico e replay",
    description: "Quem alterou o quê, marcos do projeto e o replay das sessões de programação.",
    icon: GitCommitHorizontal,
  },
  network: {
    title: "Rede de devs",
    description: "Perfis com títulos, portfólio e experiências; descubra devs por país, título e habilidade.",
    icon: UserRound,
  },
  groups: {
    title: "Grupos",
    description: "Turmas e times com projetos em comum e a atividade de todos em tempo real.",
    icon: Users,
  },
  plugins: {
    title: "Plugins e temas",
    description: "Loja com Agente Claude, Copilot, Docker, Java, Python, temas de cor como o Hacker e mais.",
    icon: Puzzle,
  },
  settings: {
    title: "Perfil e configurações",
    description: "Foto, títulos, redes sociais, experiências, portfólio, dispositivos e privacidade.",
    icon: Settings,
  },
  admin: {
    title: "Administração",
    description: "Denúncias, suspensões e auditoria.",
    icon: Shield,
  },
};
